import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Brain, FileText, ExternalLink, Link as LinkIcon } from 'lucide-react'
import Navigation from './Navigation'

/**
 * VideoLibrary Component
 * 
 * Displays a searchable grid of video content with modal expansion.
 * Features:
 * - 3-column responsive grid layout (1 on mobile, 2 on tablet, 3 on desktop)
 * - Search functionality across titles, descriptions, and published dates
 * - Modal-style card expansion for detailed video viewing (2-column width on desktop)
 * - Published date badges on each video card
 * - Category tags and duration display
 * - Vimeo video embedding with proper URL conversion
 * - Plain text previews (markdown formatting stripped) with rich text in expanded view
 * - Clear close button (red X) for expanded videos
 * 
 * @returns {JSX.Element} Video library interface
 */
const VideoLibrary = () => {
  const navigate = useNavigate()
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedCard, setExpandedCard] = useState(null)
  const [error, setError] = useState(null)
  const [searchParams] = useSearchParams()

  /**
   * Fetch videos from the backend API on component mount
   * Handles authentication token and error states
   */
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true)
        const token = localStorage.getItem('authToken')
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/videos`, {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch videos')
        }

        const data = await response.json()
        setVideos(data.videos || [])
        setError(null)
        
        // Check if a specific video should be opened from URL parameter
        const videoId = searchParams.get('video')
        if (videoId) {
          // Find the video and open it in modal
          const video = data.videos?.find(v => v.id === videoId)
          if (video) {
            setExpandedCard(videoId)
          }
        }
      } catch (error) {
        console.error('Error fetching videos:', error)
        setError('Failed to load videos. Please try again.')
        setVideos([])
      } finally {
        setLoading(false)
      }
    }

    fetchVideos()
  }, [])

  /**
   * Format date string to readable format (e.g., "Jul 31, 2025")
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date string
   */
  const formatDate = (dateString) => {
    if (!dateString) return ''
    
    // Handle date strings properly to avoid timezone issues
    // If it's a date-only string (YYYY-MM-DD), parse it as local date
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number)
      const date = new Date(year, month - 1, day) // month is 0-indexed
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
    
    // For other date formats, use the original method
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  /**
   * Parse markdown-like formatting in video descriptions
   * Supports: # headings, **bold**, line breaks, bullet points, and basic links
   * @param {string} text - Raw text with markdown formatting
   * @returns {string} HTML string with formatting applied
   */
  const parseMarkdown = (text) => {
    if (!text) return ''
    
    let html = text
      // Convert headings (# ## ### ####)
      .replace(/^#### (.+)$/gm, '<h4 class="text-lg font-semibold text-gray-900 mt-4 mb-2">$1</h4>')
      .replace(/^### (.+)$/gm, '<h3 class="text-xl font-semibold text-gray-900 mt-4 mb-2">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-semibold text-gray-900 mt-4 mb-3">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="text-3xl font-bold text-gray-900 mt-4 mb-4">$1</h1>')
      // Convert line breaks to <br> tags
      .replace(/\n/g, '<br>')
      // Convert **bold** to <strong>bold</strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Convert bullet points (- item) to <ul><li>item</li></ul>
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      // Wrap consecutive <li> elements in <ul>
      .replace(/(<li>.*<\/li>)/gs, '<ul class="list-disc list-inside my-2 space-y-1">$1</ul>')
      // Convert [text](url) to <a href="url" target="_blank" rel="noopener">text</a>
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>')
      // Convert [text](portal://path) to internal links
      .replace(/\[([^\]]+)\]\(portal:\/\/([^)]+)\)/g, '<a href="/$2" class="text-blue-600 hover:text-blue-800 underline">$1</a>')
    
    return html
  }

  /**
   * Filter videos based on search term
   * Searches across title, description, and formatted published date
   */
  const filteredVideos = videos.filter(video => {
    const searchLower = searchTerm.toLowerCase();
    const titleMatch = video.title.toLowerCase().includes(searchLower);
    const descriptionMatch = video.description.toLowerCase().includes(searchLower);
    const dateMatch = video.created_at ? formatDate(video.created_at).toLowerCase().includes(searchLower) : false;
    
    return titleMatch || descriptionMatch || dateMatch;
  })

  /**
   * Toggle video modal display state
   * Opens video in centered modal overlay for better UX
   * @param {string} videoId - ID of video to open in modal
   */
  const toggleCardExpansion = (videoId) => {
    setExpandedCard(expandedCard === videoId ? null : videoId)
  }

  /**
   * Handle keyboard events and modal accessibility
   * Provides keyboard navigation support and prevents background scrolling
   * @effect
   * @dependencies expandedCard - Re-runs when modal state changes
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && expandedCard) {
        setExpandedCard(null)
      }
    }

    if (expandedCard) {
      document.addEventListener('keydown', handleKeyDown)
      // Prevent body scroll when modal is open for better UX
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [expandedCard])

  /**
   * Strip markdown formatting from text for preview display
   * Removes headings, bold formatting, bullet points, and links while preserving text content
   * Used to show clean previews in video cards before expansion
   * @param {string} text - Text with markdown formatting
   * @returns {string} Plain text without formatting
   */
  const stripMarkdown = (text) => {
    if (!text) return ''
    return text
      // Remove headings
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold formatting
      .replace(/\*\*(.*?)\*\*/g, '$1')
      // Remove bullet points
      .replace(/^-\s+/gm, '• ')
      // Remove links but keep text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove line breaks for single line preview
      .replace(/\n/g, ' ')
      // Clean up extra spaces
      .replace(/\s+/g, ' ')
      .trim()
  }

  /**
   * Truncate description text to specified length
   * @param {string} description - Text to truncate
   * @param {number} maxLength - Maximum length before truncation
   * @returns {string} Truncated text with ellipsis
   */
  const truncateDescription = (description, maxLength = 120) => {
    if (!description) return ''
    const plainText = stripMarkdown(description)
    return plainText.length > maxLength 
      ? plainText.substring(0, maxLength) + '...'
      : plainText
  }

  /**
   * Handle content link clicks - navigates to appropriate resource based on link type
   * 
   * Content links allow videos to reference related content:
   * - Prompts: Navigate to prompt library
   * - Resources: Navigate to resources page
   * - External: Open URL in new tab
   * 
   * @param {Object} link - Content link object with:
   *   - link_type: 'prompt' | 'resource' | 'external'
   *   - content_id: UUID for prompts/resources
   *   - external_url: URL for external links
   */
  const handleContentLinkClick = (link) => {
    switch(link.link_type) {
      case 'prompt':
        navigate('/prompts');
        break;
      case 'resource': 
        navigate('/resources');
        break;
      case 'external':
        if (link.external_url) {
          window.open(link.external_url, '_blank');
        }
        break;
      default:
        console.warn('Unknown link type:', link.link_type);
    }
  }

  /**
   * Get icon for content link type
   * @param {string} linkType - Type of content link (prompt, resource, external)
   * @returns {JSX.Element} Icon component
   */
  const getContentLinkIcon = (linkType) => {
    switch(linkType) {
      case 'prompt':
        return <Brain className="w-5 h-5" />;
      case 'resource':
        return <FileText className="w-5 h-5" />;
      case 'external':
        return <ExternalLink className="w-5 h-5" />;
      default:
        return <LinkIcon className="w-5 h-5" />;
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
            <p className="text-gray-600 font-medium">Loading videos...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Videos</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Video Library</h1>
              <p className="text-lg text-gray-600">Access exclusive training content and resources</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search videos, descriptions, or dates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-14 w-full border border-gray-300 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm transition-all duration-200"
            />
          </div>
        </div>

        {/* Video Grid - Currently displays all videos, ready for pagination/virtualization */}
        {filteredVideos.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No videos found</h3>
            <p className="text-gray-600">Try adjusting your search criteria</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.map((video) => (
              <div 
                key={video.id} 
                className="bg-white border-0 rounded-3xl shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer"
                onClick={() => toggleCardExpansion(video.id)}
              >
                {/* Video Embed */}
                <div className="relative aspect-video bg-gray-100 rounded-t-3xl overflow-hidden">
                  {video.vimeo_embed_url ? (
                    <iframe
                      src={video.vimeo_embed_url}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={video.title}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m6-10V7a3 3 0 11-6 0V4a3 3 0 11-6 0v3a3 3 0 11-6 0v3" />
                      </svg>
                    </div>
                  )}
                  
                  {video.duration && (
                    <div className="absolute bottom-3 right-3 bg-black/80 text-white px-3 py-1.5 rounded-xl text-sm flex items-center space-x-1 backdrop-blur-sm">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">{video.duration}</span>
                    </div>
                  )}
                </div>
                
                {/* Card Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      {/* Training Date Badge - positioned above title */}
                      {video.training_date && (
                        <div className="mb-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {formatDate(video.training_date)}
                          </span>
                        </div>
                      )}
                      <h3 className="text-xl font-semibold text-gray-900 line-clamp-2 leading-tight">
                        {video.title}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-3 ml-3">
                      <button
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleCardExpansion(video.id)
                        }}
                        title="Watch video"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-gray-600 text-base leading-relaxed">
                    <p>{truncateDescription(video.description)}</p>
                  </div>
                  
                  {/* Categories moved to bottom with proper styling */}
                  {video.category_names && video.category_names.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex flex-wrap gap-2">
                        {video.category_names.map((categoryName, index) => (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2.5 py-1 rounded-xl text-xs font-medium"
                            style={{ 
                              backgroundColor: video.category_colors?.[index] ? `${video.category_colors[index]}20` : undefined,
                              color: video.category_colors?.[index] || undefined
                            }}
                          >
                            {categoryName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 
          Video Modal Overlay - Professional centered modal for video viewing
          
          Features:
          - Centered positioning regardless of video card location
          - Full-screen overlay with dark background
          - Click outside to close functionality
          - Escape key support for accessibility
          - Body scroll prevention when modal is open
          - Scrollable content area for long descriptions
          - Responsive design with proper max-width constraints
          - Performance optimized with cached video data
        */}
        {expandedCard && (() => {
          const selectedVideo = filteredVideos.find(v => v.id === expandedCard)
          if (!selectedVideo) return null
          
          return (
            <div 
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setExpandedCard(null)}
            >
              <div 
                className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedVideo.title}
                    </h2>
                    {selectedVideo.training_date && (
                      <div className="mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDate(selectedVideo.training_date)}
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setExpandedCard(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                    title="Close video"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

              {/* Video Content - Scrollable area for long descriptions */}
              <div className="p-6 flex-1 overflow-y-auto">
                  <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden mb-6">
                    {selectedVideo.vimeo_embed_url ? (
                      <iframe
                        src={selectedVideo.vimeo_embed_url}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title={selectedVideo.title}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m6-10V7a3 3 0 11-6 0V4a3 3 0 11-6 0v3a3 3 0 11-6 0v3" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Video Details */}
                  <div className="space-y-4">
                    {/* Description */}
                    <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-strong:text-gray-900 prose-ul:my-2 prose-li:my-1">
                      <div dangerouslySetInnerHTML={{ 
                        __html: parseMarkdown(selectedVideo.description || '') 
                      }} />
                    </div>

                    {/* Content Links Section
                         Displays related content links that admins have associated with this video.
                         Links can be prompts, resources, or external URLs, each with clickable actions. */}
                    {selectedVideo.content_links && selectedVideo.content_links.length > 0 && (
                      <div className="pt-4 border-t border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Related Content</h3>
                        <div className="space-y-3">
                          {selectedVideo.content_links.map((link, index) => (
                            <button
                              key={link.id || index}
                              onClick={() => handleContentLinkClick(link)}
                              className="w-full text-left p-4 bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 rounded-xl border border-gray-200 hover:border-blue-300 transition-all duration-200 group"
                            >
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 mt-0.5 text-blue-600 group-hover:text-blue-700">
                                  {getContentLinkIcon(link.link_type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-gray-900 group-hover:text-blue-700 mb-1">
                                    {link.link_text}
                                  </h4>
                                  {link.description && (
                                    <p className="text-sm text-gray-600 line-clamp-2">
                                      {link.description}
                                    </p>
                                  )}
                                  {link.link_type === 'external' && link.external_url && (
                                    <p className="text-xs text-gray-500 mt-1 truncate">
                                      {link.external_url}
                                    </p>
                                  )}
                                </div>
                                <div className="flex-shrink-0">
                                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Categories moved to bottom with proper styling */}
                    {selectedVideo.category_names && selectedVideo.category_names.length > 0 && (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex flex-wrap gap-2">
                          {selectedVideo.category_names.map((categoryName, index) => (
                            <span 
                              key={index}
                              className="inline-flex items-center px-2.5 py-1 rounded-xl text-xs font-medium"
                              style={{ 
                                backgroundColor: selectedVideo.category_colors?.[index] ? 
                                  `${selectedVideo.category_colors[index]}20` : undefined,
                                color: selectedVideo.category_colors?.[index] || undefined
                              }}
                            >
                              {categoryName}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })()}
      </div>
    </div>
  )
}

export default VideoLibrary

